import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Search, Plus, Beaker, Ruler, ToggleLeft, List } from 'lucide-react'
import { tokenManager } from '../../lib/api'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface SpecResult {
  id: number
  name: string
  category: string
  spec_type: string
  unit: string
  typical_min: number | null
  typical_max: number | null
  description: string
  technology: string | null
  technology_path: string | null
}

interface SpecSearchDropdownProps {
  onSelectSpec: (spec: SpecResult) => void
  onCreateNew?: (name: string) => void
  category?: string
  programId?: string
  placeholder?: string
}

const specTypeIcons: Record<string, React.ReactNode> = {
  numeric: <Ruler className="w-3.5 h-3.5" />,
  boolean: <ToggleLeft className="w-3.5 h-3.5" />,
  categorical: <List className="w-3.5 h-3.5" />,
}

const categoryColors: Record<string, string> = {
  technical: 'text-blue-400',
  manufacturing: 'text-amber-400',
  financial: 'text-green-400',
  organizational: 'text-purple-400',
  compliance: 'text-red-400',
  performance: 'text-cyan-400',
}

const SpecSearchDropdown: React.FC<SpecSearchDropdownProps> = ({
  onSelectSpec,
  onCreateNew,
  category,
  programId,
  placeholder = 'Search specs...',
}) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SpecResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const searchSpecs = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([])
      return
    }

    setIsLoading(true)
    try {
      const token = tokenManager.getAccessToken()
      const params = new URLSearchParams({ q, limit: '20' })
      if (category) params.append('category', category)
      if (programId) params.append('program_id', programId)

      const response = await fetch(
        `${API_BASE_URL}/api/scorecards/specs/search/?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.ok) {
        const data = await response.json()
        setResults(data.specs || [])
      }
    } catch (err) {
      console.error('Spec search failed:', err)
    } finally {
      setIsLoading(false)
    }
  }, [category, programId])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchSpecs(query), 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, searchSpecs])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={dropdownRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background
                     text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2
                     focus:ring-primary/50"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {isOpen && (results.length > 0 || query.length >= 2) && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg
                        max-h-80 overflow-y-auto">
          {results.map((spec) => (
            <button
              key={spec.id}
              onClick={() => {
                onSelectSpec(spec)
                setQuery('')
                setIsOpen(false)
              }}
              className="w-full text-left px-3 py-2.5 hover:bg-muted/50 border-b border-border/50
                         last:border-b-0 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className={categoryColors[spec.category] || 'text-muted-foreground'}>
                  {specTypeIcons[spec.spec_type] || <Beaker className="w-3.5 h-3.5" />}
                </span>
                <span className="text-sm font-medium text-foreground">{spec.name}</span>
                {spec.unit && (
                  <span className="text-xs text-muted-foreground">({spec.unit})</span>
                )}
              </div>
              {spec.description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1 pl-6">
                  {spec.description}
                </p>
              )}
              <div className="flex items-center gap-2 mt-0.5 pl-6 flex-wrap">
                <span className="text-[10px] text-muted-foreground uppercase">{spec.category}</span>
                <span className="text-[10px] text-muted-foreground">{spec.spec_type}</span>
                {spec.technology && (
                  <span className="text-[10px] text-primary/70">{spec.technology}</span>
                )}
                {spec.typical_min != null && spec.typical_max != null && (
                  <span className="text-[10px] text-muted-foreground">
                    Range: {spec.typical_min} - {spec.typical_max}
                    {spec.unit && ` ${spec.unit}`}
                  </span>
                )}
              </div>
            </button>
          ))}

          {/* Create new option */}
          {query.length >= 2 && onCreateNew && (
            <button
              onClick={() => {
                onCreateNew(query)
                setQuery('')
                setIsOpen(false)
              }}
              className="w-full text-left px-3 py-2.5 hover:bg-muted/50 border-t border-border
                         text-primary transition-colors"
            >
              <div className="flex items-center gap-2">
                <Plus className="w-3.5 h-3.5" />
                <span className="text-sm font-medium">Create new spec: "{query}"</span>
              </div>
            </button>
          )}

          {query.length >= 2 && results.length === 0 && !isLoading && !onCreateNew && (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              No specs found matching "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SpecSearchDropdown
